import React, { useState, useEffect, useContext } from 'react';
import {
    Box,
    Typography,
    useTheme,
    Snackbar,
    CircularProgress,
    Table,
    Sheet,
    Chip,
    RadioGroup,
    Radio,
    FormControl,
    Modal,
    ModalDialog,
    ModalClose,
    DialogTitle,
    DialogContent,
    DialogActions,
    Input,
    Button,
} from '@mui/joy';
import axios from 'axios';
import { CONTENT_WIDTH } from "../../constants/layout";
import SessionContext from "../../store";

const ReferidosPage = () => {
    const theme = useTheme();
    const { session } = useContext(SessionContext);

    const [loading, setLoading] = useState(true);
    const [info, setInfo] = useState(null);
    const [mode, setMode] = useState('separate');
    const [savingMode, setSavingMode] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: 'success', message: '' });
    const [withdrawOpen, setWithdrawOpen] = useState(false);
    const [withdrawAddress, setWithdrawAddress] = useState('');
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [withdrawing, setWithdrawing] = useState(false);

    const apiBase = process.env.REACT_APP_URL_API_CRIXTO;

    const fetchInfo = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${apiBase}orders/referrals`, {
                headers: { Authorization: `Bearer ${session.token}` }
            });
            const data = res.data?.data || {};
            setInfo(data);
            if (data.referral_payout_mode) setMode(data.referral_payout_mode);
        } catch (error) {
            setSnack({ open: true, color: 'danger', message: 'Error al obtener la información de referidos' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (session.token) fetchInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.token]);

    const handleModeChange = async (newMode) => {
        if (newMode === mode || savingMode) return;
        const previous = mode;
        setMode(newMode);
        setSavingMode(true);
        try {
            await axios.put(
                `${apiBase}orders/referrals/mode`,
                { mode: newMode },
                { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.token}` } }
            );
            setSnack({
                open: true, color: 'success',
                message: newMode === 'separate'
                    ? 'Tus comisiones de referidos se acumularán en tu wallet de referidos (retirable).'
                    : 'Tus comisiones de referidos se sumarán a tu wallet de Operador.'
            });
        } catch (error) {
            setMode(previous);
            setSnack({ open: true, color: 'danger', message: 'No se pudo actualizar la preferencia de pago' });
        } finally {
            setSavingMode(false);
        }
    };

    const handleWithdraw = async () => {
        const amt = parseFloat(withdrawAmount);
        const available = Number(info?.referral_balance) || 0;
        if (!withdrawAddress.trim()) { setSnack({ open: true, color: 'danger', message: 'Ingresa la dirección de destino' }); return; }
        if (!amt || isNaN(amt) || amt <= 0) { setSnack({ open: true, color: 'danger', message: 'Ingresa un monto válido' }); return; }
        if (amt > available) { setSnack({ open: true, color: 'danger', message: 'El monto supera tu balance de referidos' }); return; }
        setWithdrawing(true);
        try {
            await axios.post(
                `${apiBase}orders/referrals/withdraw`,
                { destination_address: withdrawAddress.trim(), amount: amt },
                { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.token}` } }
            );
            setSnack({ open: true, color: 'success', message: 'Retiro enviado. Se reflejará una vez confirmado en la red.' });
            setWithdrawOpen(false);
            setWithdrawAddress(''); setWithdrawAmount('');
            fetchInfo();
        } catch (error) {
            setSnack({ open: true, color: 'danger', message: error.response?.data?.message || 'No se pudo procesar el retiro' });
        } finally {
            setWithdrawing(false);
        }
    };

    const text = theme.vars.palette.text.main;
    const primary = theme.vars.palette.primary["500"];

    return (
        <Box sx={{ width: CONTENT_WIDTH, p: 3, fontFamily: "Montserrat" }}>
            <Typography sx={{ fontFamily: "Montserrat", fontSize: "28px", fontWeight: 700, color: primary, mb: 1 }}>
                Referidos
            </Typography>
            <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: text, mb: 3 }}>
                Gana una parte de la comisión de Muney por cada transacción exitosa de los Operadores que refieres.
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
            ) : (
                <>
                    {/* Balance de referidos */}
                    <Sheet variant="outlined" sx={{ borderRadius: "16px", p: 3, mb: 3 }}>
                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", fontWeight: 500, color: text, mb: 0.5 }}>
                            Balance de referidos
                        </Typography>
                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "34px", fontWeight: 700, color: primary }}>
                            {(Number(info?.referral_balance) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} USDT
                        </Typography>
                        {info?.referral_wallet_address && (
                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "12px", color: text, mt: 1, wordBreak: "break-all" }}>
                                Wallet de referidos: {info.referral_wallet_address}
                            </Typography>
                        )}
                        {mode === 'separate' && (Number(info?.referral_balance) || 0) > 0 && (
                            <Button
                                variant="solid"
                                color="primary"
                                onClick={() => setWithdrawOpen(true)}
                                sx={{ fontFamily: "Montserrat", borderRadius: "12px", mt: 2 }}
                            >
                                Retirar
                            </Button>
                        )}
                    </Sheet>

                    {/* Preferencia de pago */}
                    <Sheet variant="outlined" sx={{ borderRadius: "16px", p: 3, mb: 3 }}>
                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "16px", fontWeight: 700, color: primary, mb: 1 }}>
                            ¿Dónde quieres recibir tus comisiones de referidos?
                        </Typography>
                        <FormControl>
                            <RadioGroup
                                value={mode}
                                onChange={(e) => handleModeChange(e.target.value)}
                                sx={{ gap: 1.5, mt: 1 }}
                            >
                                <Radio
                                    value="separate"
                                    disabled={savingMode}
                                    label={
                                        <Box>
                                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", fontWeight: 600, color: text }}>Wallet de referidos (retirable)</Typography>
                                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "12px", color: text }}>Tus comisiones se acumulan por separado y puedes retirarlas cuando quieras.</Typography>
                                        </Box>
                                    }
                                />
                                <Radio
                                    value="mixed"
                                    disabled={savingMode}
                                    label={
                                        <Box>
                                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", fontWeight: 600, color: text }}>Wallet de Operador</Typography>
                                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "12px", color: text }}>Tus comisiones se suman directamente al saldo de tu wallet de Operador.</Typography>
                                        </Box>
                                    }
                                />
                            </RadioGroup>
                        </FormControl>
                    </Sheet>

                    {/* Lista de referidos */}
                    <Sheet variant="outlined" sx={{ borderRadius: "16px", p: 3 }}>
                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "16px", fontWeight: 700, color: primary, mb: 2 }}>
                            Tus referidos ({info?.referred_clients?.length || 0})
                        </Typography>
                        {(!info?.referred_clients || info.referred_clients.length === 0) ? (
                            <Typography sx={{ fontFamily: "Montserrat", fontSize: "14px", color: text }}>
                                Aún no tienes Operadores referidos.
                            </Typography>
                        ) : (
                            <Box sx={{ overflowX: "auto" }}>
                                <Table sx={{ fontFamily: "Montserrat", minWidth: 420 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ fontFamily: "Montserrat" }}>Operador</th>
                                            <th style={{ fontFamily: "Montserrat" }}>% Referido</th>
                                            <th style={{ fontFamily: "Montserrat" }}>Desde</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {info.referred_clients.map((c) => (
                                            <tr key={c._id || c.source}>
                                                <td style={{ fontFamily: "Montserrat" }}>{c.source}</td>
                                                <td><Chip size="sm" variant="soft" color="primary">{c.referred_percentage != null ? `${c.referred_percentage}%` : '-'}</Chip></td>
                                                <td style={{ fontFamily: "Montserrat" }}>{c.createdAt ? new Date(c.createdAt).toLocaleDateString('es-VE') : '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Box>
                        )}
                    </Sheet>
                </>
            )}

            <Modal open={withdrawOpen} onClose={() => !withdrawing && setWithdrawOpen(false)}>
                <ModalDialog variant="outlined" sx={{ minWidth: 420 }}>
                    <ModalClose disabled={withdrawing} />
                    <DialogTitle sx={{ fontFamily: "Montserrat", fontWeight: 700, color: primary }}>Retirar referidos</DialogTitle>
                    <DialogContent sx={{ mt: 1 }}>
                        <Typography sx={{ fontFamily: "Montserrat", fontSize: "13px", color: text, mb: 2 }}>
                            Disponible: {(Number(info?.referral_balance) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })} USDT
                        </Typography>
                        <Box mb={2}>
                            <Typography mb={0.5} sx={{ fontFamily: "Montserrat", fontSize: "13px", fontWeight: 500, color: text }}>Dirección de destino (USDT)</Typography>
                            <Input
                                placeholder="0x..."
                                value={withdrawAddress}
                                onChange={(e) => setWithdrawAddress(e.target.value)}
                                sx={{ fontFamily: "Montserrat", borderRadius: "12px" }}
                            />
                        </Box>
                        <Box>
                            <Typography mb={0.5} sx={{ fontFamily: "Montserrat", fontSize: "13px", fontWeight: 500, color: text }}>Monto (USDT)</Typography>
                            <Input
                                type="number"
                                placeholder="0.00"
                                value={withdrawAmount}
                                onChange={(e) => setWithdrawAmount(e.target.value)}
                                sx={{ fontFamily: "Montserrat", borderRadius: "12px" }}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="plain" color="neutral" disabled={withdrawing} onClick={() => setWithdrawOpen(false)} sx={{ fontFamily: "Montserrat" }}>Cancelar</Button>
                        <Button variant="solid" color="primary" loading={withdrawing} onClick={handleWithdraw} sx={{ fontFamily: "Montserrat", borderRadius: "12px" }}>Retirar</Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>

            <Snackbar
                open={snack.open}
                color={snack.color}
                variant="soft"
                autoHideDuration={4000}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                {snack.message}
            </Snackbar>
        </Box>
    );
};

export default ReferidosPage;
